import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import LoginPage from './pages/LoginPage';
import ExplorePage from './pages/ExplorePage';
import MapPage from './pages/MapPage';
import MatchesPage from './pages/MatchesPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import AccountInfoPage from './pages/AccountInfoPage';
import EditProfilePage from './pages/EditProfilePage';
import VenueDetailPage from './pages/VenueDetailPage';
import UserProfilePage from './pages/UserProfilePage';
import PrivacyZonesPage from './pages/PrivacyZonesPage';
import MembershipPage from './pages/MembershipPage';
import VerificationPage from './pages/VerificationPage';
import BlockedUsersPage from './pages/BlockedUsersPage';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import ReferFriendPage from './pages/ReferFriendPage';
import IncomeVerificationPage from './pages/IncomeVerificationPage';
import HotspotRequestPage from './pages/HotspotRequestPage';
import BrowseMembersPage from './pages/BrowseMembersPage';
import FavoritesPage from './pages/FavoritesPage';
import ConciergePage from './pages/ConciergePage';
import GiftsPage from './pages/GiftsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import ManageMembershipPage from './pages/ManageMembershipPage';
import PaymentWalletPage from './pages/PaymentWalletPage';
import SendDrinkPage from './pages/SendDrinkPage';
import DrinkHistoryPage from './pages/DrinkHistoryPage';
import RedeemDrinkPage from './pages/RedeemDrinkPage';
import SendSpaPage from './pages/SendSpaPage';
import SendBottlePage from './pages/SendBottlePage';
import SendDinnerPage from './pages/SendDinnerPage';
import SendEventPage from './pages/SendEventPage';
import SendRomancePage from './pages/SendRomancePage';
import SendGiftPage from './pages/SendGiftPage';
import GiftWalletPage from './pages/GiftWalletPage';
// New Gift Flow Pages
import GiftActivityPage from './pages/GiftActivityPage';
import SendGiftSelectPage from './pages/SendGiftSelectPage';
import SendGiftRecipientPage from './pages/SendGiftRecipientPage';
import SendGiftAmountPage from './pages/SendGiftAmountPage';
import SendGiftMessagePage from './pages/SendGiftMessagePage';
import SendGiftReviewPage from './pages/SendGiftReviewPage';
// Proximity Page
import ProximityPage from './pages/ProximityPage';
// Verification Flow Page
import VerificationFlowPage from './pages/VerificationFlowPage';
// Public Pages (no auth required)
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SupportPage from './pages/SupportPage';
import SafetyPage from './pages/SafetyPage';
import AdminTicketDashboard from './pages/AdminTicketDashboard';
import NearULogo from './components/NearULogo';
import ThemeToggle from './components/ThemeToggle';
import GiftingGuard from './components/GiftingGuard';
import CookieConsentBanner from './components/CookieConsentBanner';
import { ProximityProvider, useProximity } from './context/ProximityContext';
import { Heart, MapPin, Users, Calendar, MessageCircle, User, LogOut, Gift, Wine, Radio } from 'lucide-react';

function PrivateRoute({ children }) {
  const isLoggedIn = localStorage.getItem('nearU_loggedIn') === 'true';
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, [matches, query]);

  return matches;
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [badges] = useState({
    matches: 2,
    events: 1,
    messages: 3
  });

  // Get proximity state for live indicator
  let proximityEnabled = false;
  try {
    const proximityContext = useProximity();
    proximityEnabled = proximityContext?.proximityEnabled || false;
  } catch (e) {
    // Proximity context not available
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const tabs = [
    { path: '/swipe', label: 'Explore', icon: Heart, badge: 0 },
    { path: '/proximity', label: 'Proximity', icon: Radio, badge: 0, isProximity: true },
    { path: '/gifts', label: 'Gifts', icon: Gift, badge: 0 },
    { path: '/messages', label: 'Messages', icon: MessageCircle, badge: badges.messages },
    { path: '/profile', label: 'Profile', icon: User, badge: 0 },
  ];

  return (
    <nav className="bottom-nav">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const showLiveIndicator = tab.isProximity && proximityEnabled;
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={`nav-item ${isActive(tab.path) ? 'active' : ''}`}
            data-testid={`tab-${tab.label.toLowerCase()}`}
          >
            <div className="nav-icon-wrapper" style={{ position: 'relative' }}>
              <Icon className="nav-icon" size={24} />
              {tab.badge > 0 && (
                <span className="notification-badge">{tab.badge}</span>
              )}
              {showLiveIndicator && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: '#4CAF50',
                  border: '2px solid var(--bg-primary)',
                  boxShadow: '0 0 8px rgba(76, 175, 80, 0.6)',
                  animation: 'pulse 2s infinite'
                }} />
              )}
            </div>
            <span className="nav-label" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px' 
            }}>
              {showLiveIndicator && (
                <span style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  background: '#4CAF50'
                }} />
              )}
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function SidebarNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [badges] = useState({
    matches: 2,
    events: 1,
    messages: 3
  });

  // Get proximity state for live indicator
  let proximityEnabled = false;
  try {
    const proximityContext = useProximity();
    proximityEnabled = proximityContext?.proximityEnabled || false;
  } catch (e) {
    // Proximity context not available
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const tabs = [
    { path: '/swipe', label: 'Explore', icon: Heart, badge: 0 },
    { path: '/proximity', label: 'Proximity', icon: Radio, badge: 0, isProximity: true },
    { path: '/map', label: 'Map', icon: MapPin, badge: 0 },
    { path: '/gifts', label: 'Gifts', icon: Gift, badge: 0 },
    { path: '/events', label: 'Events', icon: Calendar, badge: badges.events },
    { path: '/messages', label: 'Messages', icon: MessageCircle, badge: badges.messages },
    { path: '/profile', label: 'Profile', icon: User, badge: 0 },
  ];

  const handleLogout = () => {
    localStorage.removeItem('nearU_loggedIn');
    navigate('/login');
  };

  return (
    <nav className="sidebar-nav">
      <div style={{ padding: '24px', borderBottom: '1px solid var(--border-color)' }}>
        <NearULogo size="medium" />
      </div>
      
      <div style={{ flex: 1, padding: '16px 0' }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const showLiveIndicator = tab.isProximity && proximityEnabled;
          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`sidebar-nav-item ${isActive(tab.path) ? 'active' : ''}`}
              data-testid={`sidebar-${tab.label.toLowerCase()}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Icon size={24} />
                  {showLiveIndicator && (
                    <span style={{
                      position: 'absolute',
                      top: '-3px',
                      right: '-3px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: '#4CAF50',
                      border: '2px solid var(--bg-primary)',
                      boxShadow: '0 0 8px rgba(76, 175, 80, 0.6)'
                    }} />
                  )}
                </div>
                <span style={{ fontSize: '16px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {showLiveIndicator && (
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#4CAF50'
                    }} />
                  )}
                  {tab.label}
                </span>
                {tab.badge > 0 && (
                  <span className="notification-badge" style={{ position: 'static', marginLeft: 'auto' }}>
                    {tab.badge}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ padding: '16px', borderTop: '1px solid var(--border-color)' }}>
        <button
          onClick={handleLogout}
          className="sidebar-nav-item"
          style={{ color: '#6A1F28' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <LogOut size={24} />
            <span style={{ fontSize: '16px', fontWeight: '500' }}>Logout</span>
          </div>
        </button>
      </div>
    </nav>
  );
}

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isPublicPage = ['/privacy', '/terms', '/support', '/safety'].includes(location.pathname);
  const isAdminPage = location.pathname.startsWith('/admin');
  const isVerificationPage = ['/verify', '/verification-complete'].includes(location.pathname);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Public pages - no auth required, standalone layout
  if (isPublicPage) {
    return (
      <div className="app-container">
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/safety" element={<SafetyPage />} />
        </Routes>
      </div>
    );
  }

  // Admin pages - standalone layout with auth
  if (isAdminPage) {
    return (
      <div className="app-container">
        <Routes>
          <Route path="/admin/tickets" element={<PrivateRoute><AdminTicketDashboard /></PrivateRoute>} />
        </Routes>
      </div>
    );
  }

  // Verification pages - standalone layout without bottom nav
  if (isVerificationPage) {
    return (
      <div className="app-container">
        <Routes>
          <Route path="/verify" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
          <Route path="/verification-complete" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
        </Routes>
      </div>
    );
  }

  if (isLoginPage) {
    return (
      <div className="app-container">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      </div>
    );
  }

  // Desktop Layout
  if (isDesktop) {
    return (
      <div className="desktop-layout">
        <ThemeToggle />
        <SidebarNav />
        <div className="desktop-content">
          <Routes>
            <Route path="/" element={<Navigate to="/swipe" replace />} />
            <Route path="/swipe" element={<PrivateRoute><ExplorePage /></PrivateRoute>} />
            <Route path="/proximity" element={<PrivateRoute><ProximityPage /></PrivateRoute>} />
            <Route path="/map" element={<PrivateRoute><MapPage /></PrivateRoute>} />
            <Route path="/discover" element={<Navigate to="/swipe" replace />} />
            <Route path="/venue/:venueId" element={<PrivateRoute><VenueDetailPage /></PrivateRoute>} />
            <Route path="/gifts" element={<PrivateRoute><GiftsPage /></PrivateRoute>} />
            <Route path="/payment/success" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />
            <Route path="/payment/cancel" element={<PrivateRoute><PaymentCancelPage /></PrivateRoute>} />
            <Route path="/matches" element={<PrivateRoute><MatchesPage /></PrivateRoute>} />
            <Route path="/events" element={<PrivateRoute><EventsPage /></PrivateRoute>} />
            <Route path="/events/:eventId" element={<PrivateRoute><EventDetailPage /></PrivateRoute>} />
            <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
            <Route path="/messages/:chatId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
            <Route path="/chat/:userId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
            <Route path="/profile/account" element={<PrivateRoute><AccountInfoPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
            <Route path="/profile/edit" element={<PrivateRoute><EditProfilePage /></PrivateRoute>} />
            <Route path="/profile/privacy-zones" element={<PrivateRoute><PrivacyZonesPage /></PrivateRoute>} />
            <Route path="/profile/membership" element={<PrivateRoute><MembershipPage /></PrivateRoute>} />
            <Route path="/profile/manage-membership" element={<PrivateRoute><ManageMembershipPage /></PrivateRoute>} />
            <Route path="/profile/wallet" element={<PrivateRoute><PaymentWalletPage /></PrivateRoute>} />
            <Route path="/send-drink" element={<PrivateRoute><SendDrinkPage /></PrivateRoute>} />
            <Route path="/profile/drink-history" element={<PrivateRoute><DrinkHistoryPage /></PrivateRoute>} />
            <Route path="/redeem-drink/:code" element={<RedeemDrinkPage />} />
            <Route path="/send-spa" element={<PrivateRoute><SendSpaPage /></PrivateRoute>} />
            <Route path="/send-bottle" element={<PrivateRoute><SendBottlePage /></PrivateRoute>} />
            <Route path="/send-dinner" element={<PrivateRoute><SendDinnerPage /></PrivateRoute>} />
            <Route path="/send-event" element={<PrivateRoute><SendEventPage /></PrivateRoute>} />
            <Route path="/send-romance" element={<PrivateRoute><SendRomancePage /></PrivateRoute>} />
            <Route path="/send-gift/:recipientId" element={<PrivateRoute><GiftingGuard><SendGiftPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift" element={<PrivateRoute><GiftingGuard><SendGiftPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/gift-wallet" element={<PrivateRoute><GiftingGuard><GiftWalletPage /></GiftingGuard></PrivateRoute>} />
            {/* New Gift Flow Routes - Desktop */}
            <Route path="/gift-activity" element={<PrivateRoute><GiftingGuard><GiftActivityPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/gift-categories" element={<PrivateRoute><GiftingGuard><SendGiftSelectPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift/select" element={<PrivateRoute><GiftingGuard><SendGiftSelectPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift/recipient" element={<PrivateRoute><GiftingGuard><SendGiftRecipientPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift/amount" element={<PrivateRoute><GiftingGuard><SendGiftAmountPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift/message" element={<PrivateRoute><GiftingGuard><SendGiftMessagePage /></GiftingGuard></PrivateRoute>} />
            <Route path="/send-gift/review" element={<PrivateRoute><GiftingGuard><SendGiftReviewPage /></GiftingGuard></PrivateRoute>} />
            <Route path="/profile/verification" element={<PrivateRoute><VerificationPage /></PrivateRoute>} />
            <Route path="/verify" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
            <Route path="/verification-complete" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
            <Route path="/profile/blocked-users" element={<PrivateRoute><BlockedUsersPage /></PrivateRoute>} />
            <Route path="/profile/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            <Route path="/profile/help" element={<PrivateRoute><HelpPage /></PrivateRoute>} />
            <Route path="/profile/refer" element={<PrivateRoute><ReferFriendPage /></PrivateRoute>} />
            <Route path="/profile/income-verification" element={<PrivateRoute><IncomeVerificationPage /></PrivateRoute>} />
            <Route path="/user/:userId" element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
            {/* Public Pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/safety" element={<SafetyPage />} />
          </Routes>
        </div>
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="app-container">
      <ThemeToggle />
      <div className="app-content">
        <Routes>
          <Route path="/" element={<Navigate to="/swipe" replace />} />
          <Route path="/swipe" element={<PrivateRoute><ExplorePage /></PrivateRoute>} />
          <Route path="/proximity" element={<PrivateRoute><ProximityPage /></PrivateRoute>} />
          <Route path="/map" element={<PrivateRoute><MapPage /></PrivateRoute>} />
          <Route path="/discover" element={<Navigate to="/swipe" replace />} />
          <Route path="/venue/:venueId" element={<PrivateRoute><VenueDetailPage /></PrivateRoute>} />
          <Route path="/gifts" element={<PrivateRoute><GiftsPage /></PrivateRoute>} />
          <Route path="/payment/success" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />
          <Route path="/payment/cancel" element={<PrivateRoute><PaymentCancelPage /></PrivateRoute>} />
          <Route path="/matches" element={<PrivateRoute><MatchesPage /></PrivateRoute>} />
          <Route path="/events" element={<PrivateRoute><EventsPage /></PrivateRoute>} />
          <Route path="/events/:eventId" element={<PrivateRoute><EventDetailPage /></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
          <Route path="/messages/:chatId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
          <Route path="/chat/:userId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
          <Route path="/profile/account" element={<PrivateRoute><AccountInfoPage /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/profile/edit" element={<PrivateRoute><EditProfilePage /></PrivateRoute>} />
          <Route path="/profile/privacy-zones" element={<PrivateRoute><PrivacyZonesPage /></PrivateRoute>} />
          <Route path="/profile/membership" element={<PrivateRoute><MembershipPage /></PrivateRoute>} />
          <Route path="/profile/verification" element={<PrivateRoute><VerificationPage /></PrivateRoute>} />
          <Route path="/profile/blocked-users" element={<PrivateRoute><BlockedUsersPage /></PrivateRoute>} />
          <Route path="/profile/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
          <Route path="/profile/help" element={<PrivateRoute><HelpPage /></PrivateRoute>} />
          <Route path="/profile/refer" element={<PrivateRoute><ReferFriendPage /></PrivateRoute>} />
          <Route path="/profile/income-verification" element={<PrivateRoute><IncomeVerificationPage /></PrivateRoute>} />
          <Route path="/profile/manage-membership" element={<PrivateRoute><ManageMembershipPage /></PrivateRoute>} />
          <Route path="/profile/wallet" element={<PrivateRoute><PaymentWalletPage /></PrivateRoute>} />
          <Route path="/send-drink" element={<PrivateRoute><SendDrinkPage /></PrivateRoute>} />
          <Route path="/profile/drink-history" element={<PrivateRoute><DrinkHistoryPage /></PrivateRoute>} />
          <Route path="/redeem-drink/:code" element={<RedeemDrinkPage />} />
          <Route path="/send-spa" element={<PrivateRoute><SendSpaPage /></PrivateRoute>} />
          <Route path="/send-bottle" element={<PrivateRoute><SendBottlePage /></PrivateRoute>} />
          <Route path="/send-dinner" element={<PrivateRoute><SendDinnerPage /></PrivateRoute>} />
          <Route path="/send-event" element={<PrivateRoute><SendEventPage /></PrivateRoute>} />
          <Route path="/send-romance" element={<PrivateRoute><SendRomancePage /></PrivateRoute>} />
          <Route path="/send-gift/:recipientId" element={<PrivateRoute><GiftingGuard><SendGiftPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift" element={<PrivateRoute><GiftingGuard><SendGiftPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/gift-wallet" element={<PrivateRoute><GiftingGuard><GiftWalletPage /></GiftingGuard></PrivateRoute>} />
          {/* New Gift Flow Routes */}
          <Route path="/gift-activity" element={<PrivateRoute><GiftingGuard><GiftActivityPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/gift-categories" element={<PrivateRoute><GiftingGuard><SendGiftSelectPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift/select" element={<PrivateRoute><GiftingGuard><SendGiftSelectPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift/recipient" element={<PrivateRoute><GiftingGuard><SendGiftRecipientPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift/amount" element={<PrivateRoute><GiftingGuard><SendGiftAmountPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift/message" element={<PrivateRoute><GiftingGuard><SendGiftMessagePage /></GiftingGuard></PrivateRoute>} />
          <Route path="/send-gift/review" element={<PrivateRoute><GiftingGuard><SendGiftReviewPage /></GiftingGuard></PrivateRoute>} />
          <Route path="/user/:userId" element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
          <Route path="/verify" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
          <Route path="/verification-complete" element={<PrivateRoute><VerificationFlowPage /></PrivateRoute>} />
          <Route path="/hotspot-request" element={<PrivateRoute><HotspotRequestPage /></PrivateRoute>} />
          <Route path="/browse-members" element={<PrivateRoute><BrowseMembersPage /></PrivateRoute>} />
          <Route path="/favorites" element={<PrivateRoute><FavoritesPage /></PrivateRoute>} />
          <Route path="/concierge" element={<PrivateRoute><ConciergePage /></PrivateRoute>} />
          <Route path="/gifts" element={<PrivateRoute><GiftsPage /></PrivateRoute>} />
          {/* Public Pages */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/safety" element={<SafetyPage />} />
        </Routes>
      </div>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ProximityProvider>
        <AppContent />
        <CookieConsentBanner />
      </ProximityProvider>
    </BrowserRouter>
  );
}

export default App;
